/**
 * ThemeToggle — bouton de bascule light/dark.
 *
 * - Touch target ≥ 44×44 px (WCAG 2.2 SC 2.5.8 Target Size minimum)
 * - aria-label dynamique (lecteurs d'écran)
 * - Icône Lucide qui change selon le mode résolu
 * - Anime la transition d'icône (rotation + fade)
 *
 * Refonte R3 (mai 2026) :
 *   - Style glass avec backdrop-blur cohérent avec les shells refondus
 *   - API publique conservée (className, withLabel, variant)
 */

import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/lib/theme-context';
import { clsx } from 'clsx';

interface ThemeToggleProps {
  className?: string;
  /** Si true, affiche un texte court à côté de l'icône. */
  withLabel?: boolean;
  variant?: 'icon' | 'pill';
}

export function ThemeToggle({ className, withLabel, variant = 'icon' }: ThemeToggleProps) {
  const { resolved, toggle } = useTheme();
  const isDark = resolved === 'dark';
  const targetLabel = isDark ? 'Passer en mode clair' : 'Passer en mode sombre';

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={targetLabel}
      title={targetLabel}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-r-lg transition-all duration-200',
        'min-h-[40px] min-w-[40px]',
        'border border-border bg-surface/60 backdrop-blur-glass text-ink',
        'hover:bg-surface/80 hover:border-border-strong',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        'shadow-soft',
        variant === 'pill' ? 'px-4' : 'px-2.5',
        className
      )}
    >
      {isDark ? (
        <Sun className="h-4 w-4 transition-transform duration-300" aria-hidden="true" />
      ) : (
        <Moon className="h-4 w-4 transition-transform duration-300" aria-hidden="true" />
      )}
      {withLabel && (
        <span className="text-sm font-medium">
          {isDark ? 'Clair' : 'Sombre'}
        </span>
      )}
    </button>
  );
}
